// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import ContextMenu from '../ContextMenu';
import formatDate from '../../utilities/formatDate';
import listdir from '../../utilities/listdir';

export default class Stacks extends React.Component {

    state = {
        sortBy: "",
        sortDir: 1, // 1: Ascending, -1, Descending
        cmRoot: false,
        dragOver: null,
        selectedStack: null
    }

    sort = (stacks, sortBy=undefined, sortDir=1) => {
        if (!sortBy) {
            sortBy = this.state.sortBy;
            sortDir = this.state.sortDir;
        }

        if (sortBy === "")
            return stacks;

        let sorted = stacks.slice();
        sorted.sort((a,b) => a[sortBy] > b[sortBy] ? sortDir : a[sortBy] < b[sortBy] ? -sortDir : 0);

        return sorted;
    }

    toggleHeader = (key) => () => this.setState({
        sortBy: key,
        sortDir: this.state.sortBy === key ? (this.state.sortDir > 0 ? -1 : 1) : 1
    })

    getSortArrow = (key) => {
        if (this.state.sortBy !== key)
            return "";

        return this.state.sortDir > 0 ? <>▴&nbsp;&nbsp;</> : <>▾&nbsp;&nbsp;</>;
    }

    getContextMenuOptions = () => {

        const selectedStack = this.state.selectedStack;

        // Clicked on roots
        if (this.state.cmRoot) {
            
            let opt = [
                {
                    label: 'Upload file(s)',
                    action: () => this.props.uploadFiles()
                }
            ];

            if (this.props.creatable)
                opt.push({
                    label: 'Create new stack',
                    action: () => this.props.createStack(this.getRootStack())
                });

            return opt;

        }

        // Clicked on a stack
        let opt = [
            {
                label: 'Open Stack',
                action: (stack) => this.props.history.push('/stack/' + stack._id)
            },
            {
                label: 'Open in new tab',
                action: (stack) => window.open('/stack/' + stack._id, '_blank')
            }
        ];

        if (this.props.creatable && (this.props.stacks && this.props.stacks.length > 0 && this.props.stacks[0]._id && this.props.stacks[0]._id.length > 16)){
            opt.push({
                label: 'Upload to stack',
                action: (stack) => this.props.uploadFiles(stack._id)
            },
            {
                label: (stack) => stack && stack.enabled ? "Disable stack" : "Enable stack",
                action: (stack) => this.props.updateStack(stack._id, {
                    enabled: !stack.enabled
                })
            },
            {
                label: (stack) => stack && stack.pinned ? "Unpin from quick access" : "Pin to quick access",
                action: (stack) => this.props.updateStack(stack._id, {
                    pinned: !stack.pinned
                })
            },
            {
                label: 'Copy template',
                action: (stack) => this.props.copyStack(stack._id)
            });

            //Sensitive operations
            const sensitiveOperations = [{
                label: 'Rename stack',
                action: (stack) => this.props.renameStack(stack)
            },
            {
                label: 'Move stack',
                action: (stack) => this.props.moveStack(stack._id)
            },
            {
                label: 'Delete stack',
                action: (stack) => this.props.modal("confirm", {
                    message: <>Deleting the stack <strong> {stack.relativePath} </strong>will also delete <strong> {stack.numDocuments} documents, as well as all stacks and all automation pipelines</strong> within this stack.</>,
                    action: () => this.props.deleteStack(stack._id, true)
                })
            }];

            //Provide option to lock/unlock stacks to only @stax email users and if the stack is locked then rename, delete and move options should not be available to non @stax email users
            if (this.props.user?.email?.includes("@stax.ai")) {
                opt.push({
                    label: (stack) => stack && stack.locked ? "Unlock stack" : "Lock stack",
                    action: (stack) => this.props.updateStack(stack._id, {
                        locked: !stack.locked
                    })
                },
                ...sensitiveOperations);
            }
            else {
                //We need to check if the stack is locked or not and if it is not locked then only we should provide the sensitive operations
                if (!selectedStack?.locked) {
                    opt.push(...sensitiveOperations);
                }
            }
        }
        return opt;
    }

    getRootStack = () => this.props.stacks.find(s => s._id === this.props.match.params.stackId);

    getStacks = () => {
        let dir = "";
        const root = this.getRootStack();
        if (root)
            dir = root.path;

        let stacks = listdir(this.props.stacks, dir);

        // Sort if in list view
        return this.props.view === 'list' ? this.sort(stacks) : this.sort(stacks, "pinned", -1);
    }

    syncedStacks = (source) => {
        const synced = [];
        if (this.props.team && this.props.team.sync && this.props.team.sync[source] && this.props.team.sync[source].length)
            for (const site of this.props.team.sync[source])
                if (site.folders)
                    synced.push(...site.folders.map(f => f.destination));
        return synced;
    }

    renderHeader = (label, key, className="stack-list-header-item", style={}) => (<div
        className={className + " stack-list-header-link"}
        title="Click to sort by key"
        onClick={this.toggleHeader(key)}
        style={style}
    >{this.getSortArrow(key)}{label}</div>)

    render = () => {
        const grid = this.props.view === 'grid';
        const stacks = this.getStacks();

        // Synced root locations
        const synced_dropbox = this.syncedStacks('dropbox');
        const synced_googledrive = this.syncedStacks('googledrive');
        const synced_onedrive = this.syncedStacks('onedrive');
        const synced_sharepoint = this.syncedStacks('sharepoint');
        const synced_egnyte = this.syncedStacks('egnyte');
        const synced_box = this.syncedStacks('box');

        return window.location.href.includes("/stack/") && stacks.length === 0 ? (<></>) : (<div
            className="files"
            onContextMenu={e => {
                this.setState({ cmRoot: true });
                this.openMenu(e, null);
            }}
            style={stacks.length === 0 ? { paddingBottom: 200 } : {}}
        >
            
            <h1>{this.props.title}</h1>

            {grid ? '' : <div className='stack-list-header'>
                {this.renderHeader("Stack Name", "path", "stack-list-header-left")}
                <div className="stack-list-meta">
                    {this.renderHeader("Files", "numDocuments")}
                    {this.renderHeader("Unread", "numUnread")}
                    {this.renderHeader("Last Modified", "lastModified", "stack-list-header-item", { width: '120px' })}
                </div>
            </div>}

            {stacks.map((stack,idx) => <div
                key={idx}
                className={"stack stack-" + this.props.view + (stack.enabled === false ? " stack-disabled" : "") + (this.props.selectedStack === stack._id ? " stack-selected" : "") + (stack._id.length === 24 && this.state.dragOver === stack._id ? " stack-dragover" : "")}
                onClick={() => {
                    this.props.history.push('/stack/' + stack._id)
                }}
                onContextMenu={e => {
                    this.setState({selectedStack: stack});
                    this.setState({ cmRoot: false });
                    this.openMenu(e, stack);
                }}
                draggable={false}
                // Below is no longer needed but will keep in case we want to switch back in the future. 
                onDragStart={() => this.props.moveStack(stack._id)}
                onDragEnter={() => this.setState({ dragOver: stack._id })}
                onDragLeave={() => this.setState(prevState => prevState.dragOver === stack._id ? ({ dragOver: null }) : ({}))}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.state.dragOver !== stack._id || stack._id.length !== 24) return;
                    this.setState({ dragOver: null });
                    let files = [];
                    if (e.dataTransfer.items)
                        for (let item of e.dataTransfer.items)
                            files.push(item.getAsFile());
                    this.props.uploadFilesExec(files, stack._id);
                }}
                title={stack.path + " - " + (stack.enabled ? "Click to open stack" : "This stack is disabled. You can view and modify files in it, but the bots won't move any new files to it.")}
            >
                <h1>{stack.relativePath.split('/').pop()}</h1>
                <div className={grid ? '' : 'stack-list-meta'}>
                    <h2>{stack.numDocuments}{grid ? ' documents' : ''}</h2>
                    {grid ? '' : <>
                        <div className="stack-list-meta-col">{stack.lastModified ? formatDate(stack.lastModified) : '-'}</div>
                    </>}
                </div>
                {grid && stack.pinned ? <div className="stack-pinned" /> : ""}
                {grid && <div className="stack-synced">
                    {synced_dropbox.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/dropbox.png"
                        alt="Dropbox"
                        title="Synced with Dropbox account."
                    />}

                    {synced_googledrive.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/googledrive.png"
                        alt="Google Drive"
                        title="Synced with Google Drive account."
                    />}

                    {synced_onedrive.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/onedrive.png"
                        alt="OneDrive"
                        title="Synced with OneDrive account."
                    />}

                    {synced_sharepoint.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/sharepoint.png"
                        alt="SharePoint"
                        title="Synced with a SharePoint site."
                    />}

                    {synced_egnyte.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/egnyte.png"
                        alt="Egnyte"
                        title="Synced with a Egnyte site."
                    />}

                    {synced_box.includes(stack._id) && <img
                        src="https://static.stax.ai/images/import/box.png"
                        alt="Box"
                        title="Synced with a Box account."
                    />}

                    {/* Stack flag exists? TODO @Naru */}
                </div>}
            </div>)}

            {grid && this.props.creatable && !window.location.href.includes("/stack/") && <>
                <div
                    className="stack-grid stack-grid-new"
                    onClick={() => this.props.createStack(this.getRootStack())}
                >
                    <img
                        alt="Add"
                        src={"https://static.stax.ai/images/icons/" + (this.props.user && this.props.user.enableDarkMode ? "dark/" : "") + "create.png"}
                    />
                    <h1>Create a new stack</h1>
                </div>
                {stacks.length === 0 && <div style={{
                    position: 'absolute',
                    top: 180,
                    left: 120,
                    width: 400,
                    textAlign: 'center',
                    pointerEvents: 'none'
                }}>
                    <img
                        alt="Arrow"
                        src="https://static.stax.ai/images/graphics/arrow-up.png"
                        style={{
                            marginRight: 80,
                            width: 100,
                            marginBottom: -5
                        }}
                    />
                    <h3>Click here to create a new stack!</h3>
                    <div style={{ height: 12 }} />
                    <p>Let's create a few stacks and put some example documents in them so the <strong>AI</strong> bots can learn to sort documents into them!</p>
                </div>}
            </>}

            <div style={{ clear: 'both' }} />

            {/* Stacks context menu control */}
            <ContextMenu
                load={(open,close) => {
                    this.openMenu = open;
                    this.closeMenu = close;
                }}
                options={this.getContextMenuOptions()}
            />

        </div>);
    }

}